/**
 * Video gallery cursor — re-enables the gallery's hover-cursor advance affordance on video slides.
 *
 * The Shopify theme (theme.js) restricts the <custom-cursor> overlay and the click-to-advance
 * behavior to slides whose data-media-type is "image". For autoplay-muted product videos with no
 * native chrome, that gate is unnecessary — visitors should advance past a video the same way
 * they advance past an image. We piggyback on the gallery's existing event flow:
 *
 *   1. carousel:change fires → the gallery's onMediaChange runs first and hides the cursor;
 *      our listener runs second (bubble phase) and re-shows it if the selected slide is a video.
 *   2. For click-to-advance we register in the CAPTURE phase so we run BEFORE the gallery's
 *      bubble-phase listener. If the click is on a video slide we advance + stopPropagation()
 *      so the gallery doesn't also advance after we mutated the selected cell (which would
 *      cause a double-advance and "skip" the next slide).
 *
 * Mounted from snippets/product-gallery.liquid only when the gallery is in carousel layout, has
 * more than one media, and at least one media is a video. Outside that context the file isn't
 * shipped.
 */
class VideoGalleryCursor extends HTMLElement {
  static SELECTORS = {
    gallery: 'product-gallery',
    carousel: 'scroll-carousel',
    cursor: '.custom-cursor',
    interactiveTarget: 'button, button *, a[href], a[href] *'
  };
  static EVENTS = {
    change: 'carousel:change',
    settle: 'carousel:settle',
    click: 'click'
  };
  static MEDIA_TYPE_VIDEO = 'video';
  static POINTER_FINE_QUERY = 'screen and (pointer: fine)';

  connectedCallback () {
    this._abort = new AbortController();
    this._gallery = this.closest(VideoGalleryCursor.SELECTORS.gallery);
    this._carousel = this._gallery?.querySelector(VideoGalleryCursor.SELECTORS.carousel);
    this._cursor = this._gallery?.querySelector(VideoGalleryCursor.SELECTORS.cursor);

    if (!this._carousel || !this._cursor) return;

    const signal = this._abort.signal;
    this._carousel.addEventListener(VideoGalleryCursor.EVENTS.change, () => this._syncCursorVisibility(), { signal });
    this._carousel.addEventListener(VideoGalleryCursor.EVENTS.settle, () => this._syncCursorVisibility(), { signal });
    // Capture phase so we run BEFORE the gallery's bubble-phase click handler.
    this._carousel.addEventListener(VideoGalleryCursor.EVENTS.click, (event) => this._maybeAdvance(event), { signal, capture: true });

    this._syncCursorVisibility();
  }

  disconnectedCallback () {
    this._abort?.abort();
  }

  _isOnVideoSlide () {
    return this._carousel.selectedCell?.getAttribute('data-media-type') === VideoGalleryCursor.MEDIA_TYPE_VIDEO;
  }

  _syncCursorVisibility () {
    if (this._isOnVideoSlide()) this._cursor.removeAttribute('hidden');
  }

  _maybeAdvance (event) {
    if (event.target.matches(VideoGalleryCursor.SELECTORS.interactiveTarget)) return;
    if (!window.matchMedia(VideoGalleryCursor.POINTER_FINE_QUERY).matches) return;
    if (!this._isOnVideoSlide()) return;

    // We're running in capture phase, before the gallery's bubble-phase listener. Stop the event
    // from continuing so the gallery doesn't also advance after we change the selected cell —
    // without this, clicking a video slide advances by two (mine + the gallery's).
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    if (offsetX > this._carousel.clientWidth / 2) {
      this._carousel.next();
    } else {
      this._carousel.previous();
    }
  }
}

if (!customElements.get('video-gallery-cursor')) {
  customElements.define('video-gallery-cursor', VideoGalleryCursor);
}
